// PRODUCTION — live-updates outcome bullets for a set of field changes.
import React, { memo, useMemo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import type { CollectDataTemplatePreviewChange } from 'src/interfaces/collectDataTemplatePreview'
import { humanize } from './previewFormatting'

function humanizeStates(states: string[], statuses: string[] | '*'): string {
  if (statuses === '*' && states.length === 0) {
    return 'All contractors'
  }

  const parts: string[] = []

  if (states.length > 0) {
    parts.push(states.map(humanize).join(', '))
  }

  // Render explicit (non-wildcard) statuses too — otherwise an outcome scoped
  // only by status (e.g. `statuses: ['pending']`, no states) shows no context.
  if (statuses !== '*' && statuses.length > 0) {
    parts.push(statuses.map(humanize).join(', '))
  }

  return parts.join(' · ')
}

interface OutcomeListProps {
  changes: CollectDataTemplatePreviewChange[]
}

const OutcomeListComponent = ({ changes }: OutcomeListProps) => {
  const outcomes = useMemo(() => {
    return changes.flatMap((change) => {
      return change.outcomes.map((outcome) => {
        return { outcome, propagated: change.propagated }
      })
    })
  }, [changes])

  if (outcomes.length === 0) {
    return null
  }

  return (
    <div css={tw`flex flex-col gap-2`}>
      {outcomes.map(({ outcome }, index) => {
        const context = humanizeStates(outcome.states, outcome.statuses)

        return (
          <div key={`${outcome.action}-${index}`} css={tw`flex gap-2`}>
            <span css={tw`mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary-400`} />
            <div css={tw`flex flex-col gap-0.5`}>
              <Typography variant="body2" css={tw`text-shades-800`}>
                {outcome.text}
              </Typography>
              {context.length > 0 && (
                <div css={tw`flex flex-wrap items-center gap-1.5`}>
                  <Typography variant="subtitle2" css={tw`text-shades-500`}>
                    {context}
                  </Typography>
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export const OutcomeList = memo(OutcomeListComponent)
